import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Instagram, Linkedin } from "lucide-react";
import BackButton from "@/components/BackButton";
import PageHeader from "@/components/PageHeader";
import { cleanPolicyText } from "@/utils/policyText";

const SECTIONS = [
  {
    n: "01",
    title: "Shipping Charges",
    body:
      "Free shipping on all prepaid orders above ₹999. Orders below ₹999 may attract a shipping fee displayed at checkout. Cash on Delivery, where available, may include an additional convenience fee.",
    list: null,
  },
  {
    n: "02",
    title: "Order Processing",
    body:
      "Orders are usually processed within 1–3 business days after payment confirmation. Orders placed on Sundays or public holidays will be processed on the next working day. During launch drops or promotional events, dispatch may take slightly longer.",
    list: null,
  },
  {
    n: "03",
    title: "Estimated Delivery",
    body:
      "Delivery timelines are estimates and may vary due to courier or operational delays.",
    list: [
      ["Metro cities", "2–5 business days"],
      ["Other locations", "3–7 business days"],
      ["Remote areas", "5–10 business days"],
    ],
  },
  {
    n: "04",
    title: "Order Tracking",
    body:
      "Once your order is dispatched, you will receive tracking details by email and/or SMS. You can also track your order through your account on theblackhouse.in.",
    list: null,
  },
  {
    n: "05",
    title: "Limited Edition Policy",
    body:
      "Every release by The Black House is produced in limited quantities. Once sold out, products may not be restocked. To preserve the exclusivity of every drop, all purchases are treated as final — except where a verified manufacturing defect, transit damage or incorrect product is received.",
    list: null,
  },
  {
    n: "06",
    title: "Returns & Exchanges",
    body: "We do not accept returns or exchanges for:",
    list: [
      ["—", "Change of mind"],
      ["—", "Incorrect size selected by the customer"],
      ["—", "Colour or style preference"],
      ["—", "Ordering multiple sizes for trial"],
    ],
    outro:
      "Please review the Size Guide carefully before placing your order.",
  },
  {
    n: "07",
    title: "Defective Or Incorrect Products",
    body:
      "If your order arrives damaged, defective or incorrect, notify us within 48 hours of delivery by emailing support@theblackhouse.in with your order number, photographs and preferably an unboxing video. After verification, we may provide a replacement (subject to stock), store credit or refund.",
    list: null,
  },
  {
    n: "08",
    title: "Cancellation Policy",
    body:
      "Orders may be cancelled only before dispatch. Once dispatched, orders cannot be cancelled. The Black House reserves the right to cancel orders due to pricing errors, payment verification, suspected fraud, technical issues or stock unavailability.",
    list: null,
  },
  {
    n: "09",
    title: "Refund Policy",
    body: "Refunds are issued only when:",
    list: [
      ["—", "A verified defective product cannot be replaced."],
      ["—", "An incorrect product cannot be replaced."],
      ["—", "The Black House cancels an order before dispatch."],
    ],
    outro:
      "Approved refunds are generally processed within 5–7 business days to the original payment method.",
  },
  {
    n: "10",
    title: "Size Assistance",
    body:
      "Our garments feature a gender-neutral oversized fit. If you need sizing guidance, contact our support team before ordering.",
    list: null,
  },
  {
    n: "11",
    title: "Final Sales",
    body:
      "All collections from The Black House are limited edition releases. Accordingly, all sales are final. Returns, exchanges and cancellations after dispatch are not accepted, except for verified manufacturing defects, transit damage or incorrect items delivered.",
    list: null,
  },
];

export default function Shipping() {
  useEffect(() => {
    document.title = "Shipping, Returns & Refunds — THE BLACK HOUSE";
    window.scrollTo(0, 0);
  }, []);

  const ease = [0.16, 1, 0.3, 1];

  return (
    <main
      data-testid="shipping-page"
      className="relative bg-black text-white overflow-x-hidden"
    >
      <PageHeader />
      <BackButton />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 left-5 md:top-40 md:left-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase"
      >
        Legal · Shipping
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 right-5 md:top-40 md:right-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase text-right"
      >
        Last Updated · 18 · VII · MMXXVI
      </motion.div>

      <header className="max-w-4xl mx-auto px-6 md:px-10 pt-40 md:pt-44 pb-8 md:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          className="flex flex-col items-center text-center"
        >
          <p className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[#6a6a6a] mt-0">
            — Shipping · Returns · Refunds
          </p>
          <h1
            data-testid="shipping-title"
            className="font-display font-bold text-white uppercase mt-4 text-[10vw] sm:text-[6vw] md:text-[3.6vw] leading-[0.95]"
            style={{ letterSpacing: "0.18em" }}
          >
            From Our Door
            <br />
            To Yours
          </h1>
          <div className="w-16 h-px bg-white/60 mt-8" />
          <p className="font-serif-editorial italic text-[#c9c9c4] max-w-xl mt-6 leading-[1.7] text-[15px] md:text-[17px]">
            {cleanPolicyText('How your order travels, how we handle exceptions, and what "final sale" means in a limited-edition house. Read once — remember always.')}
          </p>
        </motion.div>
      </header>

      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-0">
        <div className="border-t border-[#141414]">
          {SECTIONS.map((s, i) => (
            <motion.article
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 0.9, delay: i * 0.02, ease }}
              className="grid grid-cols-12 gap-4 md:gap-8 py-7 md:py-10 border-b border-[#141414]"
              data-testid={`shipping-section-${s.n}`}
            >
              <div className="col-span-12 md:col-span-2">
                <span className="font-mono-ui text-[11px] md:text-[12px] tracking-[0.3em] text-[#7a7a7a]">
                  § {s.n}
                </span>
              </div>
              <div className="col-span-12 md:col-span-10">
                <h2
                  className="font-display font-bold uppercase text-white text-[18px] md:text-[22px] leading-[1.2]"
                  style={{ letterSpacing: "0.16em" }}
                >
                  {s.title}
                </h2>
                <p className="font-serif-editorial text-[#c9c9c4] mt-4 leading-[1.85] text-[15px] md:text-[17px] max-w-2xl">
                  {cleanPolicyText(s.body)}
                </p>
                {s.list && (
                  <ul className="mt-5 max-w-2xl border-l border-[#1a1a1a]">
                    {s.list.map(([label, value], idx) => (
                      <li
                        key={idx}
                        className="grid grid-cols-12 gap-3 pl-4 py-2 border-b border-[#141414] last:border-b-0"
                      >
                        <span className="col-span-4 md:col-span-3 font-mono-ui text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-[#7a7a7a]">
                          {cleanPolicyText(label)}
                        </span>
                        <span className="col-span-8 md:col-span-9 font-serif-editorial text-[14px] md:text-[16px] text-[#c9c9c4]">
                          {cleanPolicyText(value)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.outro && (
                  <p className="font-serif-editorial text-[#c9c9c4] mt-5 leading-[1.85] text-[15px] md:text-[17px] max-w-2xl">
                    {cleanPolicyText(s.outro)}
                  </p>
                )}
              </div>
            </motion.article>
          ))}
        </div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8%" }}
          transition={{ duration: 0.9, ease }}
          className="grid grid-cols-12 gap-4 md:gap-8 py-6 md:py-8"
        >
          <div className="col-span-12 md:col-span-2">
            <span className="font-mono-ui text-[11px] md:text-[12px] tracking-[0.3em] text-[#7a7a7a]">
              Contact
            </span>
          </div>
          <div className="col-span-12 md:col-span-10 space-y-3 font-mono-ui text-[11px] md:text-[12px] tracking-[0.25em] uppercase text-[#c9c9c4]">
            <p>
              Website:{" "}
              <a
                href="https://www.theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                theblackhouse.in
              </a>
            </p>
            <p>
              Email:{" "}
              <a
                href="mailto:support@theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                support@theblackhouse.in
              </a>
            </p>
            <p>Business Hours: Monday–Saturday · 10:00–19:00 IST</p>
          </div>
        </motion.div>
      </section>

      <footer className="border-t border-[#141414]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 md:py-5 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link
            data-testid="shipping-back-home"
            to="/"
            className="group inline-flex items-center gap-2 font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
          >
            <ArrowLeft size={13} strokeWidth={1.4} className="transition-transform duration-500 group-hover:-translate-x-1" />
            Return To The House
          </Link>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <Link to="/about" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">About</Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link to="/terms" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Terms</Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link to="/privacy" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Privacy</Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link to="/accessibility" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Accessibility</Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link to="/security" className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500">Security</Link>
            <span className="text-[#2a2a2a]">·</span>
            <a href="https://www.instagram.com/theblackhouse.tbh" target="_blank" rel="noreferrer noopener" aria-label="Instagram" className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500">
              <Instagram size={13} strokeWidth={1.4} className="text-white/70 group-hover:text-white transition-colors duration-500" />
            </a>
            <a href="https://www.linkedin.com/company/the-black-house/" target="_blank" rel="noreferrer noopener" aria-label="LinkedIn" className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500">
              <Linkedin size={13} strokeWidth={1.4} className="text-white/70 group-hover:text-white transition-colors duration-500" />
            </a>
          </div>
        </div>
        <div className="border-t border-[#141414] px-6 md:px-10 py-3 flex items-center justify-between font-mono-ui text-[8px] md:text-[9px] tracking-[0.3em] uppercase text-[#3a3a3a]">
          <span>© MMXXVI — THE BLACK HOUSE</span>
          <span>All rights are gathered.</span>
        </div>
      </footer>
    </main>
  );
}
